/* eslint-disable no-shadow */
import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import FocusTrap from 'focus-trap-react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
  MODAL_MODES, setMultiStepModalMode, setMultiStepModalOpenState, generateShareableListLinkThunk, resetListById,
} from '../../../reducers/favoriteSlice';
import TranslationProvider, { getTranslation } from '../../../providers/TranslationProvider';

import InfoIcon from '../../../svg/icons/ic_info-outline_red_35x35.svg';
import FavoriteHeart from '../../../svg/icons/ui-favorite.svg';
import Delete from '../../../svg/icons/ui-delete.svg';
import Edit from '../../../svg/icons/ui-edit.svg';
import Spinner from '../../../svg/icons/processing-spinner.svg';
import shareSVG from '../../../svg/icons/ic_share_20x20.svg';
import Ellipsis from '../../../svg/icons/more-horizontal.svg';
import Plus from '../../../svg/icons/add.svg';
import FavoritesBanner from '../FavoritesView/FavoritesBanner';
import Check from '../../../svg/icons/checkmark.svg';
import Button from '../../Button';
import Link from '../../Link';
import Icon from '../../Icon';
import Loader from '../../Loader';
import FavoriteVenueCard from '../../FavoriteVenueCard';
import ClampLines from '../../ClampLines';
import ProfilePhoto from '../../ProfilePhoto';

import './MultipleListsDetailView.scss';
import MultiStepListModal from '../../MultiStepListModal';
import { getImageUrl, getNumOfVenuesContent } from '../../../utils';
import { getUserProfileLocations, postUserProfileLocations } from '../../../api';
import handleCreatedByText from '../../../utils/handleCreatedByText';
import { redirectToSanitizedUrl } from '../../../utils/sanitizeTargetUrl';

function MultipleListsDetailView(
  {
    $rootScope,
    Analytics,
    createdBy,
    count,
    description,
    event,
    generateShareableListLinkThunk,
    isError,
    isFollowing,
    isLegacyHitlist,
    isLoading,
    isMobile,
    listId,
    urlSlug,
    name,
    onCopyListClick,
    onFavoriteClick,
    onFollowListClick,
    responsiveImages,
    setMultiStepModalMode,
    setMultiStepModalOpenState,
    venues,
  }
) {
  const [state, setState] = useState({
    isMarketable: undefined,
    isShareLoading: false,
    isFollowLoading: false,
  });
  const [openMenu, setOpenMenu] = useState(false);

  const menu = useRef();

  const handleClickOutside = (e) => {
    if (!menu?.current?.contains(e.target)) {
      setOpenMenu(false);
    }
  };

  const fetchShareableLink = async () => {
    if (listId) {
      const generateShareableListLinkThunkResponse = await generateShareableListLinkThunk(listId);
      const generatedShareableListUrlSlug = generateShareableListLinkThunkResponse?.payload?.url_slug;
      const shareableListUrl = `${window.location.origin}/shared/lists/${generatedShareableListUrlSlug}`;
      setState({
        ...state,
        shareableListUrl,
      });
    }
    if (urlSlug) {
      const shareableListUrl = `${window.location.origin}/shared/lists/${urlSlug}`;
      setState({
        ...state,
        shareableListUrl,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    resetListById();
    fetchShareableLink();

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [listId, urlSlug]);

  const handleFavoriteClick = async (event) => {
    await onFavoriteClick(event);
  };

  const isOptedIn = async () => {
    const profileLocations = await getUserProfileLocations();
    setState({
      ...state,
      isMarketable: profileLocations?.is_marketable,
      isToggleEnabled: profileLocations?.is_marketable,
      profileLocations,
    });
  };

  const toggleIsMarketable = () => {
    setState({
      ...state,
      isToggleEnabled: !state.isToggleEnabled,
    });
    postUserProfileLocations({
      locationCodes: state.profileLocations?.locations,
      isMarketable: !state.isMarketable,
      primaryLocationCode: state.profileLocations?.primary,
    });
  };

  const renderFavoritesBanner = () => (
    <FavoritesBanner
      toggleIsMarketable={toggleIsMarketable}
      isMarketable={state.isToggleEnabled}
    />
  );

  const handleRefreshClick = () => {
    const { search } = window.location;
    redirectToSanitizedUrl(`/account/lists/${listId}${search}`);
  };

  const editList = () => {
    const modalMode = isLegacyHitlist ? MODAL_MODES.HIT_LIST : MODAL_MODES.EDIT_LIST;
    setMultiStepModalMode(modalMode);
    setMultiStepModalOpenState(true);
    setOpenMenu(false);
  };

  const deleteList = () => {
    const modalMode = isLegacyHitlist ? MODAL_MODES.HIT_LIST : MODAL_MODES.DELETE_LIST;
    setMultiStepModalMode(modalMode);
    setMultiStepModalOpenState(true);
    setOpenMenu(false);
  };

  const copyList = () => {
    onCopyListClick();
    setOpenMenu(false);
  };

  const onShareButtonClick = useCallback(() => {
    if (state.shareableListUrl) {
      setState({ ...state, isShareLoading: true });
      const SHOW_COPIED_CONFIRMATION_TEXT_DURATION = 2000;
      navigator.clipboard.writeText(state.shareableListUrl).then(() => {
        setState({
          ...state,
          showIsCopiedText: true,
        });
        setTimeout(() => {
          setState({
            ...state,
            showIsCopiedText: false,
            isShareLoading: false,
          });
        }, SHOW_COPIED_CONFIRMATION_TEXT_DURATION);
      });
      Analytics.event('share list');
    }
  }, [listId, urlSlug, state]);

  const onFollowButtonClick = async (follow) => {
    setState({ ...state, isFollowLoading: true });
    await onFollowListClick(follow);
    setState({ ...state, isFollowLoading: false });
  };

  const renderActionButtons = () => (
    <div className="MultipleListsDetailView__action-buttons-wrapper">
      <Button
        className="MultipleListsDetailView__share-menu-button"
        onClick={onShareButtonClick}
        data-test-id="list-share-button"
        sm
      >
        {state.isShareLoading ? (
          <Spinner className="MultipleListsDetailView__loading-icon" height="15px" width="15px" fill="#EAEAEA" />
        ) : (
          <Icon
            className="MultipleListsDetailView__share-icon"
            component={shareSVG}
            name="upload"
            height="15px"
            width="15px"
          />
        )}
        {state.showIsCopiedText ? (
          <TranslationProvider translationKey="button_copied_bang">
            <span aria-live="polite">Copied!</span>
          </TranslationProvider>
        ) : (
          <TranslationProvider translationKey="share">
            <span>
              Share
            </span>
          </TranslationProvider>
        )}
      </Button>
      <Button
        aria-expanded={openMenu}
        aria-label={getTranslation({
          translationKey: 'lists.menu',
          fallback: 'Lists Menu',
        })}
        className="MultipleListsDetailView__ellipsis-menu-button"
        data-test-id="MultipleListsDetailView__ellipsis-menu-button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpenMenu(!openMenu);
        }}
        type="button"
        variant="no-style"
      >
        <Icon
          name="ellipsis"
          component={Ellipsis}
          height="20px"
          width="20px"
        />
      </Button>

      <FocusTrap
        active={openMenu}
        focusTrapOptions={{
          allowOutsideClick: true,
          clickOutsideDeactivates: true,
          initialFocus: 'false',
          onDeactivate: () => {
            setTimeout(() => { setOpenMenu(false); }, 200);
          },
        }}
      >
        <div className={cx('MultipleListsDetailView__menu-container', {
          'MultipleListsDetailView__menu-container--open': openMenu,
        })}
        >
          {createdBy && (
            <>
              <Button
                className="MultipleListsDetailView__copy-button"
                data-test-id="MultipleListsDetailView__copy-button"
                variant="no-style"
                onClick={copyList}
                type="button"
              >
                <Icon
                  name="add"
                  component={Plus}
                  height="20px"
                  width="20px"
                />
                <TranslationProvider
                  translationKey="lists.copy"
                >
                  <div className="MultipleListsDetailView__copy-button-text">
                    Copy to My Lists
                  </div>
                </TranslationProvider>
              </Button>
            </>
          )}

          {!createdBy && (
            <>
              <Button
                className={cx('MultipleListsDetailView__edit-button', {
                  'MultipleListsDetailView__edit-button--hitlist': isLegacyHitlist,
                  'MultipleListsDetailView__edit-button--not-hitlist': !isLegacyHitlist,
                })}
                data-test-id="MultipleListsDetailView__edit-button"
                onClick={editList}
                type="button"
              >
                <Icon
                  name="edit"
                  component={Edit}
                  height="20px"
                  width="20px"
                />
                <TranslationProvider
                  translationKey="lists.edit"
                >
                  <div className="MultipleListsDetailView__edit-button-text">
                    Edit
                  </div>
                </TranslationProvider>
              </Button>
              <Button
                className={cx('MultipleListsDetailView__delete-button', {
                  'MultipleListsDetailView__delete-button--hitlist': isLegacyHitlist,
                  'MultipleListsDetailView__delete-button--not-hitlist': !isLegacyHitlist,
                })}
                data-test-id="MultipleListsDetailView__delete-button"
                onClick={deleteList}
                type="button"
              >
                <Icon
                  name="delete"
                  component={Delete}
                  height="20px"
                  width="20px"
                />
                <TranslationProvider
                  translationKey="lists.delete"
                >
                  <div className="MultipleListsDetailView__delete-button-text">
                    Delete
                  </div>
                </TranslationProvider>
              </Button>
            </>
          )}
        </div>
      </FocusTrap>
    </div>
  );

  const renderCreatedBy = () => {
    if (createdBy) {
      const { display, profilePicture } = createdBy;
      return (
        <div className="MultipleListsDetailView__created-by">
          <div className="MultipleListsDetailView__created-by-profile-pic">
            <ProfilePhoto url={profilePicture} size={20} />
          </div>
          <span>{`${handleCreatedByText(display)} • ${getNumOfVenuesContent(count)}`}</span>
        </div>
      );
    }

    return getNumOfVenuesContent(count);
  };

  const renderHeader = () => (
    <>
      <div className="MultipleListsDetailView__back-link">
        <Link href="/account/lists">‹ All Lists</Link>
      </div>
      {(isLegacyHitlist && state.isMarketable === false) && renderFavoritesBanner()}
      <div className="MultipleListsDetailView__venue-count-ellipsis-menu" ref={menu}>
        <div className="MultipleListsDetailView__venue-title-wrapper">
          <h2 className="MultipleListsDetailView__title">{name}</h2>
          {description
            && (
              <div className="MultipleListsDetailView__description">
                <ClampLines
                  buttons
                  ellipsis="..."
                  id="description"
                  lessText={getTranslation({
                    translationKey: 'less',
                    fallback: 'Less',
                  })}
                  lines={3}
                  moreText={getTranslation({
                    translationKey: 'more',
                    fallback: 'More',
                  })}
                  text={description}
                />
              </div>
            )}
          <div className="MultipleListsDetailView__venue-count">
            {renderCreatedBy()}
          </div>
        </div>
        {!isMobile && renderActionButtons()}
      </div>
      {isMobile && <div className="MultipleListsDetailView__mobile-action-buttons-wrapper">{renderActionButtons()}</div>}
      <MultiStepListModal Analytics={Analytics} description={description} listId={listId} urlSlug={urlSlug} name={name} />
    </>
  );

  const renderNoVenues = () => (
    <div className="MultipleListsDetailView__error">
      <div className="MultipleListsDetailView__error-icon">
        <Icon name="favorite-heart" component={FavoriteHeart} width="45px" height="45px" />
      </div>
      <h3 className="MultipleListsDetailView__error-title">
        {getTranslation({
          translationKey: 'errors.lists.no_data_title',
          fallback: 'No Venues Added to List',
        })}
      </h3>
    </div>
  );

  if (isLoading) return <Loader />;

  if (state.isMarketable === undefined) isOptedIn();

  if (isError) {
    return (
      <div className="MultipleListsDetailView__error">
        <div className="MultipleListsDetailView__error-icon">
          <Icon component={InfoIcon} width="35px" height="35px" />
        </div>
        <h3 className="MultipleListsDetailView__error-title">
          {getTranslation({
            translationKey: 'errors.lists.no_data_title',
            fallback: 'Oops! Unable to load page.',
          })}
        </h3>
        <p className="MultipleListsDetailView__error-body">
          {getTranslation({
            translationKey: 'errors.lists.no_data_body',
            fallback: 'Please try again.',
          })}
        </p>
        <Button
          sm
          className="MultipleListsDetailView__error-cta"
          onClick={handleRefreshClick}
        >
          {getTranslation({
            translationKey: 'errors.lists.no_data_cta',
            fallback: 'Try Again',
          })}
        </Button>
      </div>
    );
  }

  const hasVenues = (venues.length > 0);

  const thumbnail = (responsiveImages) ? getImageUrl(responsiveImages) : undefined;
  setTimeout(() => {
    $rootScope.metadata.title = `Lists - ${name}`;
    $rootScope.metadata.image = thumbnail;
    $rootScope.metadata.image_facebook = thumbnail;
    $rootScope.metadata.image_twitter = thumbnail;
  }, 100);

  return (
    <div className="MultipleListsDetailView">
      {renderHeader()}
      {createdBy && (
        <div className="MultipleListsDetailView__follow-button-container">
          {isFollowing
            && (
            <Button
              className="MultipleListsDetailView__follow-button"
              variant="secondary"
              onClick={() => {
                onFollowButtonClick(false);
              }}
            >
              {state.isFollowLoading ? (
                <Spinner className="MultipleListsDetailView__loading-icon" height="15px" width="15px" fill="#EAEAEA" />
              ) : (
                <Icon name="check" component={Check} width="15" height="15px" />
              )}
              <span>
                {getTranslation({
                  translationKey: 'following_list',
                  fallback: 'Following',
                })}
              </span>
            </Button>
            )}
          {!isFollowing
            && (
            <Button
              className="MultipleListsDetailView__not-following-button"
              onClick={() => {
                onFollowButtonClick(true);
              }}
            >
              {state.isFollowLoading ? (
                <Spinner className="MultipleListsDetailView__loading-icon" height="15px" width="15px" fill="#EAEAEA" />
              ) : (
                <Icon name="check" component={Check} width="15" height="15px" />
              )}
              <span>
                {getTranslation({
                  translationKey: 'follow_list',
                  fallback: 'Follow List',
                })}
              </span>
            </Button>
            )}
        </div>
      )}
      {!hasVenues && renderNoVenues()}
      {hasVenues && (
        venues.map((restaurant) => (
          <>
            <FavoriteVenueCard
              key={restaurant.id}
              venue={{
                ...restaurant,
                collections: [],
                favorite: restaurant.favorite,
                image: getImageUrl(restaurant.responsiveImages),
                slots: [],
              }}
              onFavoriteClick={(favorite) => handleFavoriteClick(favorite)}
              isMobile={isMobile}
              event={event}
            />
          </>
        )))}
    </div>
  );
}

MultipleListsDetailView.propTypes = {
  $rootScope: PropTypes.shape({
    metadata: PropTypes.shape({
      image: PropTypes.string,
      image_facebook: PropTypes.string,
      image_twitter: PropTypes.string,
      title: PropTypes.string,
    }),
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  count: PropTypes.number,
  createdBy: PropTypes.shape({
    display: PropTypes.string,
    profilePicture: PropTypes.string,
  }),
  description: PropTypes.string,
  event: PropTypes.func.isRequired,
  generateShareableListLinkThunk: PropTypes.func.isRequired,
  isError: PropTypes.bool,
  isFollowing: PropTypes.bool,
  isLegacyHitlist: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  listId: PropTypes.string,
  urlSlug: PropTypes.string,
  name: PropTypes.string,
  onCopyListClick: PropTypes.func.isRequired,
  onFavoriteClick: PropTypes.func.isRequired,
  onFollowListClick: PropTypes.func.isRequired,
  responsiveImages: PropTypes.shape({}),
  setMultiStepModalMode: PropTypes.func.isRequired,
  setMultiStepModalOpenState: PropTypes.func.isRequired,
  venues: PropTypes.shape([]),
};

MultipleListsDetailView.defaultProps = {
  count: 0,
  createdBy: undefined,
  description: undefined,
  isError: false,
  isFollowing: true,
  isLegacyHitlist: false,
  isLoading: false,
  isMobile: false,
  listId: undefined,
  urlSlug: undefined,
  name: '',
  responsiveImages: undefined,
  venues: [],
};

const mapDispatchToProps = {
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  generateShareableListLinkThunk,
};

export { MultipleListsDetailView as ComponentUnderTest };
export default connect(null, mapDispatchToProps)(MultipleListsDetailView);
